import {useCallback} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectSiteId} from '@packages/systems/designer/site-data';
import webflowFetch from '@packages/utilities/fetch';

type RecipientVerificationStatusResult = {
  // Whether this site requires recipients to verify their email.
  enforced: boolean;
  // Lowercased emails (among the current destinations) that are verified.
  verifiedEmails: Set<string>;
  // Whether the site can send confirmation emails yet (it must be published).
  canSendConfirmations: boolean;
  isLoading: boolean;
};

const EMPTY_VERIFIED = new Set<string>();

type RecipientVerificationResponse = {
  enforced: boolean;
  verified: string[];
  canSendConfirmations: boolean;
};

/**
 * Fetches which of the form's current recipient emails are verified for the
 * workspace, and whether this site enforces verification — used to render the
 * "verified" checkmark next to each recipient. The server is the source of
 * truth for whether the site is enforced.
 */
export const useRecipientVerificationStatus = (
  emailDestinations: string[]
): RecipientVerificationStatusResult => {
  const siteId = useDesignerSelector(selectSiteId);

  // Stable key: normalized, deduped, sorted destinations.
  const emailsKey = Array.from(
    new Set(
      emailDestinations
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    )
  )
    .sort()
    .join(',');

  const fetchStatuses =
    useCallback(async (): Promise<RecipientVerificationResponse> => {
      const response = await webflowFetch(
        `/api/v1/site/${siteId}/form-recipient-verifications?emails=${encodeURIComponent(
          emailsKey
        )}`
      );
      if (!response.ok) {
        throw new Error(
          `Fetching recipient verification status error: ${response.statusText}`
        );
      }
      return response.json();
    }, [siteId, emailsKey]);

  const {data, isLoading} = useQuery<RecipientVerificationResponse>({
    queryKey: ['recipient-verification', siteId, emailsKey],
    queryFn: fetchStatuses,
    enabled: Boolean(siteId) && emailsKey.length > 0,
    staleTime: 30 * 1000,
    // Keep the prior result while refetching after the recipient list changes,
    // so site-level `enforced` doesn't flicker to false when a recipient is added
    // (which would drop the checkmarks and suppress the save toast). react-query
    // v4's option for this is keepPreviousData (v5's placeholderData identity).
    keepPreviousData: true,
  });

  return {
    enforced: data?.enforced ?? false,
    canSendConfirmations: data?.canSendConfirmations ?? false,
    verifiedEmails: data?.verified
      ? new Set(data.verified.map((email) => email.toLowerCase()))
      : EMPTY_VERIFIED,
    isLoading,
  };
};
